import { apiClient } from "../../apiClient";
import { ApiResponse } from "../../../types/api";
import {
  Country,
  CountryApiResponse,
  CreateCountryRequest,
  UpdateCountryRequest,
  GetCountriesParams,
  GetCountriesResponse,
} from "./type";

class CountryService {
  private transformCountry(apiCountry: CountryApiResponse): Country {
    return {
      ...apiCountry,
    };
  }

  async getCountries(
    params: GetCountriesParams = {},
  ): Promise<GetCountriesResponse> {
    try {
      const queryParams = new URLSearchParams();

      if (params.skip !== undefined)
        queryParams.append("skip", params.skip.toString());
      if (params.limit !== undefined)
        queryParams.append("limit", params.limit.toString());
      if (params.search) queryParams.append("search", params.search);

      const response = await apiClient.get<ApiResponse<CountryApiResponse[]>>(
        `/countries/?${queryParams.toString()}`,
      );

      const countries = response.data.data.map((country) =>
        this.transformCountry(country),
      );

      return {
        countries,
        total: countries.length,
        skip: params.skip || 0,
        limit: params.limit || 25,
      };
    } catch (error) {
      console.error("Failed to fetch countries:", error);
      throw error;
    }
  }

  async getCountry(id: string): Promise<Country> {
    try {
      const response = await apiClient.get<ApiResponse<CountryApiResponse>>(
        `/countries/${id}`,
      );

      return this.transformCountry(response.data.data);
    } catch (error) {
      console.error(`Failed to fetch country ${id}:`, error);
      throw error;
    }
  }

  async createCountry(data: CreateCountryRequest): Promise<Country> {
    try {
      const response = await apiClient.post<ApiResponse<CountryApiResponse>>(
        "/countries/",
        data,
      );

      return this.transformCountry(response.data.data);
    } catch (error) {
      console.error("Failed to create country:", error);
      throw error;
    }
  }

  async updateCountry(
    countryId: string,
    data: UpdateCountryRequest,
  ): Promise<Country> {
    try {
      const response = await apiClient.put<ApiResponse<CountryApiResponse>>(
        `/countries/${countryId}`,
        data,
      );
      return this.transformCountry(response.data.data);
    } catch (error) {
      console.error(`Failed to update country ${countryId}:`, error);
      throw error;
    }
  }

  async deleteCountry(id: string): Promise<void> {
    try {
      await apiClient.delete(`/countries/${id}`);
    } catch (error) {
      console.error(`Failed to delete country ${id}:`, error);
      throw error;
    }
  }
}

export const countryService = new CountryService();

export type {
  GetCountriesParams,
  GetCountriesResponse,
  CreateCountryRequest,
  UpdateCountryRequest,
};
